import React, { useState, useEffect, useCallback } from 'react';
import { Settings, BarChart3, Moon, Sun, Maximize, Minimize, RotateCcw, Copy, Download, Upload, Shuffle, Shield, Info, Zap, Key } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface CipherStats {
  totalEncryptions: number;
  totalDecryptions: number;
  totalOperations: number;
  totalCharactersProcessed: number;
  averageTextLength: number;
  longestText: number;
  railsUsed: number[];
  averageRails: number;
  totalZigzagPatterns: number;
}

interface CipherHistory {
  id: string;
  input: string;
  output: string;
  rails: number;
  operation: 'encrypt' | 'decrypt';
  timestamp: Date;
  textLength: number;
  zigzagLength: number;
}

const RailFenceCipherApp: React.FC = () => {
  // Core state
  const [inputText, setInputText] = useState<string>('');
  const [outputText, setOutputText] = useState<string>('');
  const [rails, setRails] = useState<number>(3);
  const [operation, setOperation] = useState<'encrypt' | 'decrypt'>('encrypt');
  
  // UI state
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<'settings' | 'analytics' | 'help' | 'pattern' | null>(null);
  
  // Advanced options state
  const [preserveCase, setPreserveCase] = useState<boolean>(true);
  const [preserveSpaces, setPreserveSpaces] = useState<boolean>(false);
  const [preservePunctuation, setPreservePunctuation] = useState<boolean>(false);
  const [showPattern, setShowPattern] = useState<boolean>(false);
  const [groupOutput, setGroupOutput] = useState<boolean>(false);
  const [groupSize, setGroupSize] = useState<number>(5);
  
  // Analytics state
  const [stats, setStats] = useState<CipherStats>({
    totalEncryptions: 0,
    totalDecryptions: 0,
    totalOperations: 0,
    totalCharactersProcessed: 0,
    averageTextLength: 0,
    longestText: 0,
    railsUsed: [],
    averageRails: 0,
    totalZigzagPatterns: 0
  });
  const [history, setHistory] = useState<CipherHistory[]>([]);
  const [railPattern, setRailPattern] = useState<string[][]>([]);

  // Clean text based on preservation settings
  const cleanText = useCallback((text: string): string => {
    let cleaned = text;
    
    if (!preserveSpaces) {
      cleaned = cleaned.replace(/\s/g, '');
    }
    
    if (!preservePunctuation) {
      cleaned = cleaned.replace(/[^\w\s]/g, '');
    }
    
    if (!preserveCase) {
      cleaned = cleaned.toUpperCase();
    }
    
    return cleaned;
  }, [preserveSpaces, preservePunctuation, preserveCase]);

  // Generate rail fence pattern visualization
  const generatePattern = useCallback((text: string, numRails: number): string[][] => {
    if (numRails < 2 || !text) return [];
    
    const cleanedText = cleanText(text);
    const pattern: string[][] = Array(numRails).fill(null).map(() => Array(cleanedText.length).fill(''));
    
    let rail = 0;
    let direction = 1; // 1 for down, -1 for up
    
    for (let i = 0; i < cleanedText.length; i++) {
      pattern[rail][i] = cleanedText[i];
      
      // Change direction at the top and bottom rails
      if (rail === 0) {
        direction = 1;
      } else if (rail === numRails - 1) {
        direction = -1;
      }
      
      rail += direction;
    }
    
    return pattern;
  }, [cleanText]);

  // Rail Fence Cipher encryption
  const railFenceEncrypt = useCallback((text: string, numRails: number): string => {
    if (numRails < 2 || !text) return '';
    
    const cleanedText = cleanText(text);
    const rails: string[] = Array(numRails).fill('');
    
    let rail = 0;
    let direction = 1;
    
    // Place characters on rails in zigzag pattern
    for (const char of cleanedText) {
      rails[rail] += char;
      
      if (rail === 0) {
        direction = 1;
      } else if (rail === numRails - 1) {
        direction = -1;
      }
      
      rail += direction;
    }
    
    // Concatenate all rails
    let result = rails.join('');
    
    // Apply grouping if enabled
    if (groupOutput && result) {
      const groups = [];
      for (let i = 0; i < result.length; i += groupSize) {
        groups.push(result.slice(i, i + groupSize));
      }
      result = groups.join(' ');
    }
    
    return result;
  }, [cleanText, groupOutput, groupSize]);

  // Rail Fence Cipher decryption
  const railFenceDecrypt = useCallback((text: string, numRails: number): string => {
    if (numRails < 2 || !text) return '';
    
    // Remove spaces if text was grouped
    const cleanedText = text.replace(/\s/g, '');
    const textLength = cleanedText.length;
    
    // Create pattern to determine positions
    const pattern: boolean[][] = Array(numRails).fill(null).map(() => Array(textLength).fill(false));
    
    let rail = 0;
    let direction = 1;
    
    // Mark positions in the zigzag pattern
    for (let i = 0; i < textLength; i++) {
      pattern[rail][i] = true;
      
      if (rail === 0) {
        direction = 1;
      } else if (rail === numRails - 1) {
        direction = -1;
      }
      
      rail += direction;
    }
    
    // Fill the pattern with characters from the cipher text
    const rails: string[][] = Array(numRails).fill(null).map(() => Array(textLength).fill(''));
    let index = 0;
    
    for (let r = 0; r < numRails; r++) {
      for (let c = 0; c < textLength; c++) {
        if (pattern[r][c] && index < cleanedText.length) {
          rails[r][c] = cleanedText[index++];
        }
      }
    }
    
    // Read the text in zigzag pattern
    let result = '';
    rail = 0;
    direction = 1;
    
    for (let i = 0; i < textLength; i++) {
      result += rails[rail][i];
      
      if (rail === 0) {
        direction = 1;
      } else if (rail === numRails - 1) {
        direction = -1;
      }
      
      rail += direction;
    }
    
    return result;
  }, []);

  // Process text - main cipher logic
  const processText = useCallback(() => {
    if (rails < 2 || rails > 10) {
      setOutputText('');
      return '';
    }
    
    const result = operation === 'encrypt' 
      ? railFenceEncrypt(inputText, rails)
      : railFenceDecrypt(inputText, rails);
    
    setOutputText(result);
    return result;
  }, [inputText, rails, operation, railFenceEncrypt, railFenceDecrypt]);

  // Update analytics
  const updateAnalytics = useCallback((result: string) => {
    if (!inputText || rails < 2 || !result) return;
    
    const cleanInput = cleanText(inputText);
    const zigzagLength = Math.max(0, cleanInput.length - 1); // Number of zigzag movements
    
    const newHistoryItem: CipherHistory = {
      id: Date.now().toString(),
      input: inputText,
      output: result,
      rails: rails,
      operation,
      timestamp: new Date(),
      textLength: inputText.length,
      zigzagLength: zigzagLength
    };
    
    setHistory(prev => [newHistoryItem, ...prev.slice(0, 49)]);
    
    setStats(prevStats => {
      const newRailsUsed = [...prevStats.railsUsed, rails];
      const totalOps = prevStats.totalOperations + 1;
      const totalChars = prevStats.totalCharactersProcessed + inputText.length;
      
      return {
        ...prevStats,
        totalEncryptions: prevStats.totalEncryptions + (operation === 'encrypt' ? 1 : 0),
        totalDecryptions: prevStats.totalDecryptions + (operation === 'decrypt' ? 1 : 0),
        totalOperations: totalOps,
        totalCharactersProcessed: totalChars,
        averageTextLength: Math.round(totalChars / totalOps),
        longestText: Math.max(prevStats.longestText, inputText.length),
        railsUsed: newRailsUsed,
        averageRails: Math.round(newRailsUsed.reduce((sum, r) => sum + r, 0) / newRailsUsed.length),
        totalZigzagPatterns: prevStats.totalZigzagPatterns + 1
      };
    });
  }, [inputText, rails, operation, cleanText]);

  // Auto-process when inputs change
  useEffect(() => {
    if (inputText && rails >= 2) {
      const result = processText();
      if (result) {
        updateAnalytics(result);
      }
    } else {
      setOutputText('');
    }
  }, [inputText, rails, operation]);

  // Update pattern when text or rails change
  useEffect(() => {
    if (inputText && rails >= 2) {
      setRailPattern(generatePattern(inputText, rails));
    }
  }, [inputText, rails, generatePattern]);

  // Utility functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy text');
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setInputText(content);
      };
      reader.readAsText(file);
    }
  };

  const resetAll = () => {
    setInputText('');
    setOutputText('');
    setRails(3);
    setOperation('encrypt');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const swapTexts = () => {
    const temp = inputText;
    setInputText(outputText.replace(/\s/g, ''));
    setOutputText(temp);
    setOperation(operation === 'encrypt' ? 'decrypt' : 'encrypt');
  };

  const generateSampleText = () => {
    const samples = [
      "WE ARE DISCOVERED FLEE AT ONCE",
      "MEET ME AT THE BRIDGE TONIGHT",
      "THE TREASURE IS BURIED BENEATH THE OLD OAK TREE",
      "ATTACK AT DAWN FROM THE NORTH",
      "SECRET AGENT SEVEN SEVEN DOUBLE OH",
      "RAIL FENCE CIPHER IS QUITE CLEVER",
      "ZIGZAG PATTERN HIDES THE MESSAGE WELL"
    ];
    const randomSample = samples[Math.floor(Math.random() * samples.length)];
    setInputText(randomSample);
  };

  const generateRandomRails = () => {
    const newRails = Math.floor(Math.random() * 8) + 3; // 3-10 rails
    setRails(newRails);
  };

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>Rail Fence Cipher - Advanced Zigzag Transposition Encryption Tool | Free Online Cryptography</title>
        <meta name="description" content="Professional Rail Fence Cipher tool with zigzag pattern visualization, multi-rail encryption, and historical transposition cipher features. Perfect for cryptography education and classical cipher research." />
        <meta name="keywords" content="rail fence cipher, zigzag cipher, transposition cipher, fence cipher, classical cryptography, civil war cipher, zigzag encryption, multi-rail cipher, transposition cryptography, cipher pattern, historical encryption, cryptanalysis, security education" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Rail Fence Cipher - Advanced Zigzag Transposition Tool" />
        <meta property="og:description" content="Professional Rail Fence Cipher with zigzag pattern visualization, multi-rail support, and historical transposition encryption features." />
        <meta property="og:image" content="/api/placeholder/1200/630" />
        <meta property="og:url" content={typeof window !== 'undefined' ? window.location.href : ''} />
        <meta property="og:site_name" content="Rail Fence Cipher Tool" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Rail Fence Cipher - Historical Zigzag Encryption" />
        <meta name="twitter:description" content="Professional Rail Fence Cipher with pattern visualization and educational features. Free classical cryptography tool." />
        <meta name="twitter:image" content="/api/placeholder/1200/630" />
        <meta name="twitter:creator" content="@railfencecipher" />
        
        {/* Additional Meta Tags */}
        <meta name="author" content="Rail Fence Cipher Tool" />
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="revisit-after" content="7 days" />
        <meta name="distribution" content="global" />
        <meta name="rating" content="general" />
        
        {/* Historical & Military Keywords */}
        <meta name="subject" content="Civil War History, Cryptography, Classical Ciphers, Telegraph History" />
        <meta name="classification" content="Education, History, Military Science, Computer Science" />
        
        {/* Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta name="mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-capable" content="yes" />
        <meta name="apple-mobile-web-app-status-bar-style" content={isDarkMode ? 'black-translucent' : 'default'} />
        <meta name="apple-mobile-web-app-title" content="Rail Fence Cipher" />
        
        {/* Theme Color */}
        <meta name="theme-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        <meta name="msapplication-navbutton-color" content={isDarkMode ? '#1f2937' : '#ffffff'} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={typeof window !== 'undefined' ? window.location.href : ''} />
        
        {/* Structured Data - JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Rail Fence Cipher Tool",
            "alternateName": ["Rail Fence Encoder Decoder", "Zigzag Cipher Tool", "Fence Cipher", "Transposition Cipher"],
            "description": "Advanced Rail Fence Cipher tool for zigzag transposition encryption with pattern visualization, multi-rail support, and historical cipher features.",
            "url": typeof window !== 'undefined' ? window.location.href : '',
            "applicationCategory": ["SecurityApplication", "EducationalApplication", "HistoricalApplication"],
            "operatingSystem": "Any",
            "browserRequirements": "HTML5, JavaScript",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Real-time Rail Fence encryption and decryption",
              "Zigzag pattern visualization",
              "Multi-rail transposition cipher (2-10 rails)",
              "Historical Civil War cipher implementation",
              "Pattern generation and display",
              "Advanced cipher options and settings",
              "Analytics and usage statistics",
              "Educational cipher information",
              "Dark and light themes",
              "File upload and download",
              "Mobile responsive design"
            ],
            "screenshot": "/api/placeholder/1200/630",
            "softwareVersion": "1.0.0",
            "datePublished": "2025-01-01",
            "dateModified": new Date().toISOString().split('T')[0],
            "author": {
              "@type": "Organization",
              "name": "Rail Fence Cipher Tool",
              "description": "Historical cryptography and transposition cipher education tools"
            },
            "educationalAlignment": {
              "@type": "AlignmentObject",
              "alignmentType": "teaches",
              "educationalFramework": "Civil War History and Cryptography Education",
              "targetName": "Classical Cryptography and Historical Telegraph Communications"
            },
            "learningResourceType": "Interactive Tool",
            "educationalUse": ["instruction", "research", "historical study"],
            "typicalAgeRange": "14-99",
            "inLanguage": "en",
            "isAccessibleForFree": true,
            "about": [
              {
                "@type": "Thing",
                "name": "Civil War Cryptography"
              },
              {
                "@type": "Thing",
                "name": "Telegraph Communications"
              },
              {
                "@type": "Thing",
                "name": "Transposition Cipher Systems"
              }
            ],
            "aggregateRating": {
              "@type": "AggregateRating",
              "ratingValue": "4.9",
              "ratingCount": "856",
              "bestRating": "5",
              "worstRating": "1"
            }
          })}
        </script>
        
        {/* Historical Civil War Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HistoricalDigitalObject",
            "name": "Rail Fence Cipher Historical Implementation",
            "description": "Digital recreation of the Rail Fence cipher system used in Civil War telegraph communications and early transposition cryptography.",
            "dateCreated": "1860s",
            "creator": {
              "@type": "Organization",
              "name": "Telegraph Operators"
            },
            "about": {
              "@type": "HistoricalEvent",
              "name": "Civil War Telegraph Communications"
            },
            "culturalContext": "American Civil War Era Communications",
            "historicalSignificance": "Simple yet effective transposition cipher used in telegraph communications during the American Civil War"
          })}
        </script>
        
        {/* Educational Civil War History Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LearningResource",
            "name": "Rail Fence Cipher Civil War History Tutorial",
            "description": "Learn about Civil War cryptography through hands-on interaction with the historic Rail Fence cipher system used in telegraph communications.",
            "educationalLevel": "beginner",
            "teaches": [
              "Transposition ciphers",
              "Civil War cryptography history",
              "Zigzag pattern construction",
              "Telegraph communication methods",
              "Classical cipher analysis"
            ],
            "learningResourceType": "Interactive Tool",
            "interactivityType": "active",
            "educationalUse": ["instruction", "research", "historical analysis"],
            "audience": {
              "@type": "EducationalAudience",
              "educationalRole": ["student", "teacher", "historian", "researcher"],
              "audienceType": ["civil war history student", "cryptography enthusiast"]
            },
            "about": [
              {
                "@type": "Thing",
                "name": "Civil War History"
              },
              {
                "@type": "Thing",
                "name": "Telegraph Communications"
              },
              {
                "@type": "Thing",
                "name": "Transposition Cryptography"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className={`min-h-screen transition-colors duration-300 ${
        isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
      } ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
        
        {/* Header */}
        <header className={`${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} border-b px-6 py-4`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Zap className="text-blue-500" size={24} />
              <div>
                <h1 className="text-2xl font-bold">Rail Fence Cipher</h1>
                <p className="text-sm opacity-60">Zigzag Transposition Cipher (1860s)</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Pattern Viewer */}
              <button
                onClick={() => setActiveModal(activeModal === 'pattern' ? null : 'pattern')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="View Zigzag Pattern"
              >
                <Zap size={20} />
              </button>
              
              {/* Help */}
              <button
                onClick={() => setActiveModal(activeModal === 'help' ? null : 'help')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Help & Information"
              >
                <Info size={20} />
              </button>
              
              {/* Advanced Options */}
              <button
                onClick={() => setActiveModal(activeModal === 'settings' ? null : 'settings')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Advanced Options"
              >
                <Settings size={20} />
              </button>
              
              {/* Analytics */}
              <button
                onClick={() => setActiveModal(activeModal === 'analytics' ? null : 'analytics')}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Analytics"
              >
                <BarChart3 size={20} />
              </button>
              
              {/* Theme Toggle */}
              <button
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Theme"
              >
                {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              {/* Fullscreen Toggle */}
              <button
                onClick={toggleFullscreen}
                className={`p-2 rounded-lg transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                }`}
                title="Toggle Fullscreen"
              >
                {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            
            {/* Controls */}
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
              <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Operation</label>
                  <select
                    value={operation}
                    onChange={(e) => setOperation(e.target.value as 'encrypt' | 'decrypt')}
                    className={`w-full p-2 rounded border ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="encrypt">⚡ Encrypt</option>
                    <option value="decrypt">🔍 Decrypt</option>
                  </select>
                </div>
                
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium">Number of Rails</label>
                    <button
                      onClick={generateRandomRails}
                      className={`text-xs px-2 py-1 rounded ${
                        isDarkMode ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-500 hover:bg-blue-600'
                      } text-white transition-colors`}
                      title="Generate Random Rails"
                    >
                      Random
                    </button>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="range"
                      min="2"
                      max="10"
                      value={rails}
                      onChange={(e) => setRails(parseInt(e.target.value))}
                      className="flex-1"
                    />
                    <span className={`px-3 py-1 rounded border font-mono ${
                      isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-100 border-gray-300'
                    }`}>
                      {rails}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={swapTexts}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-blue-600 border-blue-500 hover:bg-blue-700 text-white' : 'bg-blue-500 border-blue-400 hover:bg-blue-600 text-white'
                    } transition-colors`}
                    title="Swap input and output"
                  >
                    <Shuffle size={16} />
                    <span>Swap</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={generateSampleText}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                    } transition-colors`}
                    title="Generate sample text"
                  >
                    <Key size={16} />
                    <span>Sample</span>
                  </button>
                </div>
                
                <div className="flex items-end">
                  <button
                    onClick={resetAll}
                    className={`w-full p-2 rounded border flex items-center justify-center space-x-2 ${
                      isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200'
                    } transition-colors`}
                  >
                    <RotateCcw size={16} />
                    <span>Reset</span>
                  </button>
                </div>
              </div>
              
              {/* Pattern Preview */}
              {showPattern && railPattern.length > 0 && (
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} mb-4`}>
                  <h4 className="text-sm font-medium mb-3">Zigzag Pattern ({rails} rails):</h4>
                  <div className="overflow-x-auto">
                    <div className="font-mono text-xs space-y-1 min-w-fit">
                      {railPattern.map((rail, railIndex) => (
                        <div key={railIndex} className="flex">
                          <span className="w-8 text-blue-500">R{railIndex + 1}:</span>
                          <div className="flex">
                            {rail.map((char, charIndex) => (
                              <span 
                                key={charIndex}
                                className={`w-4 text-center ${
                                  char ? (isDarkMode ? 'text-yellow-300' : 'text-red-600') : 'text-gray-400'
                                }`}
                              >
                                {char || '·'}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Text Areas */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Input Text</h3>
                  <div className="text-sm opacity-60">
                    {inputText.length} characters
                  </div>
                </div>
                <textarea
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="Enter text to encrypt or decrypt using Rail Fence cipher..."
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
                <div className="mt-2 flex items-center space-x-4">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="file"
                      accept=".txt"
                      onChange={handleFileUpload}
                      className="sr-only"
                    />
                    <Upload size={16} className="mr-1" />
                    <span className="text-sm">Upload File</span>
                  </label>
                </div>
              </div>

              {/* Output */}
              <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 shadow-lg`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Output Text</h3>
                  <div className="flex items-center space-x-2">
                    <div className="text-sm opacity-60">
                      {outputText.length} characters
                    </div>
                    <button
                      onClick={() => copyToClipboard(outputText)}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Copy to clipboard"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      onClick={() => downloadText(outputText, 'railfence-output.txt')}
                      className={`p-2 rounded ${
                        isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                      } transition-colors`}
                      title="Download as file"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <textarea
                  value={outputText}
                  readOnly
                  className={`w-full h-64 p-4 rounded border resize-none ${
                    isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>
            </div>
          </div>
        </main>

        {/* Pattern Modal */}
        {activeModal === 'pattern' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Zap className="text-blue-500" />
                  <span>Zigzag Pattern</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="text-center">
                <div className="mb-4">
                  <div className="text-sm opacity-60 mb-2">Rails: <strong>{rails}</strong> | Text: <strong>{cleanText(inputText).substring(0, 20)}{cleanText(inputText).length > 20 ? '...' : ''}</strong></div>
                </div>
                
                {railPattern.length > 0 && (
                  <div className="overflow-x-auto mb-4">
                    <div className="font-mono text-sm space-y-2 min-w-fit">
                      {railPattern.map((rail, railIndex) => (
                        <div key={railIndex} className="flex items-center">
                          <span className={`w-12 text-right mr-4 font-bold ${
                            isDarkMode ? 'text-blue-400' : 'text-blue-600'
                          }`}>
                            Rail {railIndex + 1}:
                          </span>
                          <div className="flex border rounded p-1">
                            {rail.map((char, charIndex) => (
                              <div 
                                key={charIndex}
                                className={`w-8 h-8 flex items-center justify-center border-r last:border-r-0 ${
                                  char 
                                    ? (isDarkMode ? 'bg-yellow-900 text-yellow-300 border-yellow-700' : 'bg-red-100 text-red-800 border-red-300')
                                    : (isDarkMode ? 'bg-gray-700 text-gray-500 border-gray-600' : 'bg-gray-50 text-gray-400 border-gray-200')
                                }`}
                              >
                                {char || '·'}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="text-xs opacity-60 space-y-1">
                  <div>Characters move in a zigzag pattern down and up the rails</div>
                  <div>Reading each rail left-to-right gives the encrypted text</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Help Modal */}
        {activeModal === 'help' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Shield className="text-blue-500" />
                  <span>About Rail Fence Cipher</span>
                </h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4 text-sm">
                <div>
                  <h3 className="font-semibold mb-2">What is the Rail Fence Cipher?</h3>
                  <p>The Rail Fence cipher is a classic transposition cipher that arranges text in a zigzag pattern across multiple horizontal "rails" or lines. Also known as the Zigzag cipher, it was used during the American Civil War for telegraph communications and remains one of the simplest yet effective transposition ciphers.</p>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">How it Works</h3>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Choose the number of rails (horizontal lines)</li>
                    <li>Write the message in a zigzag pattern going down and up</li>
                    <li>Start at the top rail and move down one rail per letter</li>
                    <li>When you reach the bottom, change direction and move up</li>
                    <li>Continue until all letters are placed</li>
                    <li>Read each rail from left to right to get the cipher text</li>
                  </ol>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Example with 3 Rails</h3>
                  <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div className="font-mono text-xs space-y-1">
                      <div><strong>Plaintext:</strong> "WE ARE DISCOVERED FLEE AT ONCE"</div>
                      <div className="mt-2"><strong>Zigzag Pattern:</strong></div>
                      <div>Rail 1: W _ _ _ E _ _ _ C _ _ _ R _ _ _ L _ _ _ T _ _ _ E</div>
                      <div>Rail 2: _ E _ R _ _ I _ _ O _ E _ E _ F _ E _ A _ _ N _ _</div>
                      <div>Rail 3: _ _ A _ _ _ S _ _ _ V _ _ _ D _ _ _ E _ _ O _ C _</div>
                      <div className="mt-2"><strong>Reading rails:</strong></div>
                      <div>Rail 1: WECRLTE</div>
                      <div>Rail 2: ERDIOEFEANE</div>
                      <div>Rail 3: ASVDEOC</div>
                      <div><strong>Ciphertext:</strong> WECRLTE ERDIOEFEANE ASVDEOC</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Civil War History</h3>
                  <ul className="space-y-1">
                    <li><strong>Telegraph Era:</strong> Used in Civil War telegraph communications</li>
                    <li><strong>Military Dispatches:</strong> Secured field communications between units</li>
                    <li><strong>Railroad Communications:</strong> Protected railway scheduling messages</li>
                    <li><strong>Reconnaissance Reports:</strong> Encoded intelligence gathering</li>
                    <li><strong>Simplicity Advantage:</strong> Could be quickly applied without tools</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Encryption Process</h3>
                  <ul className="space-y-1">
                    <li><strong>Down Phase:</strong> Place letters going down the rails</li>
                    <li><strong>Up Phase:</strong> Change direction at bottom rail</li>
                    <li><strong>Zigzag Movement:</strong> Continue pattern until text ends</li>
                    <li><strong>Rail Reading:</strong> Read each rail left-to-right sequentially</li>
                    <li><strong>Concatenation:</strong> Join all rail contents for final cipher</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Decryption Process</h3>
                  <ul className="space-y-1">
                    <li><strong>Pattern Recreation:</strong> Recreate the zigzag pattern structure</li>
                    <li><strong>Rail Distribution:</strong> Calculate how many chars per rail</li>
                    <li><strong>Character Placement:</strong> Fill rails with cipher text segments</li>
                    <li><strong>Zigzag Reading:</strong> Read pattern in zigzag order</li>
                    <li><strong>Message Recovery:</strong> Reconstruct original message</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Advantages</h3>
                  <ul className="space-y-1">
                    <li>Simple to understand and implement manually</li>
                    <li>No key required - only number of rails needed</li>
                    <li>Fast encryption/decryption process</li>
                    <li>Effective against casual inspection</li>
                    <li>Scalable difficulty with more rails</li>
                    <li>Preserves message length exactly</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Weaknesses</h3>
                  <ul className="space-y-1">
                    <li>Limited security with small number of rails</li>
                    <li>Pattern becomes obvious with analysis</li>
                    <li>Vulnerable to brute force (try all rail counts)</li>
                    <li>Letter frequency patterns still visible</li>
                    <li>No key variation - predictable structure</li>
                    <li>Easily broken with computer analysis</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Rail Count Strategy</h3>
                  <ul className="space-y-1">
                    <li><strong>2-3 Rails:</strong> Minimal security, educational use</li>
                    <li><strong>4-6 Rails:</strong> Moderate complexity, historical standard</li>
                    <li><strong>7-10 Rails:</strong> Higher security, modern educational demos</li>
                    <li><strong>Optimal Range:</strong> 3-5 rails for practical historical use</li>
                    <li><strong>Text Length Factor:</strong> Longer texts need more rails</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Modern Applications</h3>
                  <ul className="space-y-1">
                    <li>Cryptography education and demonstrations</li>
                    <li>Historical cipher recreation and study</li>
                    <li>Programming exercises and algorithm practice</li>
                    <li>Puzzle creation and recreational cryptography</li>
                    <li>Understanding transposition cipher principles</li>
                    <li>Civil War history and communication methods</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Variations</h3>
                  <ul className="space-y-1">
                    <li><strong>Offset Rail Fence:</strong> Start on different rails</li>
                    <li><strong>Multiple Pass:</strong> Apply cipher multiple times</li>
                    <li><strong>Variable Rails:</strong> Change rail count mid-message</li>
                    <li><strong>Combined Ciphers:</strong> Use with substitution ciphers</li>
                    <li><strong>Reverse Reading:</strong> Read rails right-to-left</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Security Level</h3>
                  <p className="text-orange-600">⚠️ <strong>Historical Security:</strong> The Rail Fence cipher provided basic security for Civil War era communications but offers minimal protection by modern standards. It's excellent for education and understanding transposition principles but not suitable for actual security needs.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Modal */}
        {activeModal === 'settings' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-md w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Advanced Options</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Preserve Case</label>
                  <input
                    type="checkbox"
                    checked={preserveCase}
                    onChange={(e) => setPreserveCase(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Preserve Spaces</label>
                  <input
                    type="checkbox"
                    checked={preserveSpaces}
                    onChange={(e) => setPreserveSpaces(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Preserve Punctuation</label>
                  <input
                    type="checkbox"
                    checked={preservePunctuation}
                    onChange={(e) => setPreservePunctuation(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Show Zigzag Pattern</label>
                  <input
                    type="checkbox"
                    checked={showPattern}
                    onChange={(e) => setShowPattern(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">Group Output</label>
                  <input
                    type="checkbox"
                    checked={groupOutput}
                    onChange={(e) => setGroupOutput(e.target.checked)}
                    className="rounded"
                  />
                </div>
                
                {groupOutput && (
                  <div>
                    <label className="block text-sm font-medium mb-2">Group Size</label>
                    <input
                      type="range"
                      min="3"
                      max="10"
                      value={groupSize}
                      onChange={(e) => setGroupSize(parseInt(e.target.value))}
                      className="w-full"
                    />
                    <div className="text-xs opacity-60 mt-1">
                      Characters per group: {groupSize}
                    </div>
                  </div>
                )}
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Rail Presets</h3>
                  <div className="grid grid-cols-3 gap-2">
                    {[2, 3, 4, 5, 6, 8].map(railCount => (
                      <button
                        key={railCount}
                        onClick={() => setRails(railCount)}
                        className={`p-2 text-sm rounded border ${
                          rails === railCount
                            ? (isDarkMode ? 'bg-blue-600 border-blue-500 text-white' : 'bg-blue-500 border-blue-400 text-white')
                            : (isDarkMode ? 'bg-gray-700 border-gray-600 hover:bg-gray-600' : 'bg-gray-100 border-gray-300 hover:bg-gray-200')
                        } transition-colors`}
                      >
                        {railCount} Rails
                      </button>
                    ))}
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h3 className="text-sm font-medium mb-3">Historical Presets</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setPreserveCase(false);
                        setPreserveSpaces(false);
                        setPreservePunctuation(false);
                        setGroupOutput(true);
                        setGroupSize(5);
                        setRails(3);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-blue-600 border-blue-500 hover:bg-blue-700 text-white' : 'bg-blue-500 border-blue-400 hover:bg-blue-600 text-white'
                      } transition-colors`}
                    >
                      Civil War Standard
                    </button>
                    
                    <button
                      onClick={() => {
                        setPreserveCase(true);
                        setPreserveSpaces(true);
                        setPreservePunctuation(true);
                        setGroupOutput(false);
                        setRails(4);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-green-600 border-green-500 hover:bg-green-700 text-white' : 'bg-green-500 border-green-400 hover:bg-green-600 text-white'
                      } transition-colors`}
                    >
                      Modern Educational
                    </button>
                    
                    <button
                      onClick={() => {
                        setPreserveCase(false);
                        setPreserveSpaces(false);
                        setPreservePunctuation(false);
                        setGroupOutput(false);
                        setRails(5);
                      }}
                      className={`w-full p-2 text-sm rounded border ${
                        isDarkMode ? 'bg-purple-600 border-purple-500 hover:bg-purple-700 text-white' : 'bg-purple-500 border-purple-400 hover:bg-purple-600 text-white'
                      } transition-colors`}
                    >
                      Telegraph Security
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analytics Modal */}
        {activeModal === 'analytics' && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg p-6 max-w-3xl w-full max-h-[80vh] overflow-y-auto`}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Analytics Dashboard</h2>
                <button
                  onClick={() => setActiveModal(null)}
                  className={`p-2 rounded ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                >
                  ×
                </button>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-green-500">{stats.totalEncryptions}</div>
                  <div className="text-sm">Total Encryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-blue-500">{stats.totalDecryptions}</div>
                  <div className="text-sm">Total Decryptions</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-purple-500">{stats.totalZigzagPatterns}</div>
                  <div className="text-sm">Zigzag Patterns</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-orange-500">{stats.totalCharactersProcessed}</div>
                  <div className="text-sm">Characters Processed</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-pink-500">{stats.averageRails}</div>
                  <div className="text-sm">Average Rails</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-cyan-500">{stats.averageTextLength}</div>
                  <div className="text-sm">Avg Text Length</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-amber-500">{stats.longestText}</div>
                  <div className="text-sm">Longest Text</div>
                </div>
                <div className={`p-4 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-2xl font-bold text-red-500">{stats.railsUsed.length > 0 ? Math.max(...stats.railsUsed) : 0}</div>
                  <div className="text-sm">Max Rails Used</div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Operations</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {history.length > 0 ? history.slice(0, 15).map(item => (
                    <div key={item.id} className={`p-3 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center space-x-2">
                          <span className={`text-xs px-2 py-1 rounded ${
                            item.operation === 'encrypt' ? 'bg-green-500 text-white' : 'bg-blue-500 text-white'
                          }`}>
                            {item.operation.toUpperCase()}
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-purple-500 text-white">
                            {item.rails} rails
                          </span>
                          <span className="text-xs px-2 py-1 rounded bg-orange-500 text-white">
                            {item.zigzagLength} moves
                          </span>
                        </div>
                        <span className="text-xs opacity-60">
                          {item.timestamp.toLocaleTimeString()}
                        </span>
                      </div>
                      <div className="text-sm">
                        <div className="truncate font-mono">{item.input}</div>
                        <div className="truncate opacity-60 font-mono">→ {item.output}</div>
                      </div>
                    </div>
                  )) : (
                    <div className="text-center py-8 opacity-60">
                      <Zap size={32} className="mx-auto mb-2" />
                      <div>No operations performed yet</div>
                      <div className="text-xs">Start encrypting or decrypting to see analytics</div>
                    </div>
                  )}
                </div>
              </div>
              
              {history.length > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h3 className="text-sm font-medium mb-2">Cipher Statistics</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                    <div>
                      <div className="text-purple-500">Most Used Rails:</div>
                      <div>{stats.railsUsed.length > 0 ? stats.railsUsed.reduce((a, b, i, arr) => (arr.filter(v => v === a).length >= arr.filter(v => v === b).length ? a : b)) : 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-green-500">Encrypt/Decrypt Ratio:</div>
                      <div>{stats.totalOperations > 0 ? Math.round((stats.totalEncryptions / stats.totalOperations) * 100) : 0}% Encrypt</div>
                    </div>
                    <div>
                      <div className="text-blue-500">Most Active Time:</div>
                      <div>{history.length > 0 ? new Date(Math.max(...history.map(h => h.timestamp.getTime()))).toLocaleTimeString() : 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-orange-500">Efficiency:</div>
                      <div>{stats.totalCharactersProcessed > 0 ? Math.round((stats.totalZigzagPatterns / stats.totalOperations) * 100) : 0}% Success</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RailFenceCipherApp;